import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Breadcrumbs,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Share,
  LocationOn,
  Visibility,
  ShoppingCart,
  Edit,
  Delete,
  NavigateNext,
  ArrowBack,
  Chat,
  ChevronRight,
  ChevronLeft,
  Fullscreen,
  CalendarToday,
  Category,
  NewReleases,
  Inventory2,
  Devices,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { productService, reservationService, wishlistService, communityService, userService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useCommunityModeration } from '../hooks/useCommunityModeration';
import SimpleImageViewer from '../components/SimpleImageViewer';
import { formatCurrency, formatDate, formatNumber } from '../utils/i18n';
import { buildGoMessagesPath } from '../utils/redirect';
import { getImageUrl } from '../utils/imageUtils';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import UserSchedulesSection from '../components/sedes/UserSchedulesSection';

const CONDITION_LABELS = {
  NEW: 'Nuevo',
  LIKE_NEW: 'Como nuevo',
  GOOD: 'Bueno',
  FAIR: 'Regular',
  POOR: 'Malo',
};

const STATUS_CONFIG_TW = {
  AVAILABLE: { label: 'Disponible', icon: 'check_circle', classes: 'bg-primary/10 text-primary' },
  RESERVED: { label: 'Reservado', icon: 'schedule', classes: 'bg-yellow-100 text-yellow-800' },
  SOLD: { label: 'Vendido', icon: 'sell', classes: 'bg-error/10 text-error' },
};

const STATUS_CONFIG_MUI = {
  AVAILABLE: { label: 'Disponible', color: 'success' },
  RESERVED: { label: 'Reservado', color: 'warning' },
  SOLD: { label: 'Vendido', color: 'error' },
};

const NAV_ITEMS = [
  { path: '/', icon: 'home', label: 'Home' },
  { path: '/communities', icon: 'group_work', label: 'Circles' },
  { path: '/reservations', icon: 'swap_horiz', label: 'Mis compraventas' },
  { path: '/messages', icon: 'menu_book', label: 'Messages' },
  { path: '/wishlist', icon: 'bookmark', label: 'Saved' },
];

const DESIGN_NAMES = [
  'TW Con Sidebar',        // 0 — Tailwind with sidebar (default)
  'MUI Gray Background',   // 1 — gray bg + white cards
  'MUI + Sidebar',         // 2 — MUI with DashboardSidebar
  'MUI Shadow',            // 3 — shadows instead of borders
  'MUI Original',          // 4 — borders
  'MUI Shadow + Sidebar',  // 5 — shadow + sidebar
  'MUI Gray + Sidebar',    // 6 — gray bg + sidebar
  'TW Con Header',         // 7 — Tailwind with header
  'TW Sin Header',         // 8 — Tailwind no header
];

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const themeVars = getThemeVars(ACTIVE_THEME);
  const muiTheme = useTheme();

  const [designVariant, setDesignVariant] = useState(0); // 0 = "TW Con Sidebar"
  const [product, setProduct] = useState(null);
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [reservationDialog, setReservationDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // ID de la reserva recién creada — cuando está seteado, el modal de
  // reservar muta a estado "éxito" y muestra acciones (chat / ver reservas)
  // en vez del botón de confirmar. Sin esto el modal quedaba mostrando
  // datos privados del vendedor (mail / WhatsApp), lo que no queremos.
  const [reservedId, setReservedId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [productImages, setProductImages] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const [moderationContext, setModerationContext] = useState(null);
  const [kickDialog, setKickDialog] = useState(false);
  const [banDialog, setBanDialog] = useState(false);
  const [banReason, setBanReason] = useState('');

  const { kickFromCommunity, banFromCommunity } = useCommunityModeration();

  useEffect(() => { loadProduct(); }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await productService.getProductById(id);
      const productData = response.data.data || response.data;
      setProduct(productData);
      setSelectedImage(0);

      try {
        const imagesResponse = await productService.getProductImages(id);
        const imagesData = imagesResponse.data.data || imagesResponse.data || [];
        setProductImages(imagesData);
      } catch { setProductImages([]); }

      if (productData.communityId) {
        try {
          const communityResponse = await communityService.getCommunityById(productData.communityId);
          setCommunity(communityResponse.data.data || communityResponse.data);
        } catch {}
      }

      if (user && productData.publisherId && productData.publisherId !== user?.id) {
        try {
          const modCtxResponse = await userService.getModerationContext(productData.publisherId);
          setModerationContext(modCtxResponse.data.data || modCtxResponse.data);
        } catch { setModerationContext(null); }
      }

      if (user) {
        try {
          const wishlistResponse = await wishlistService.checkWishlistStatus(id);
          const wishlistData = wishlistResponse.data.data || wishlistResponse.data;
          setIsWishlisted(wishlistData.inWishlist || false);
        } catch { setIsWishlisted(false); }
      }

      if (productData.communityId) {
        try {
          const relatedResponse = await productService.getProductsByCommunity(productData.communityId);
          setRelatedProducts(
            (relatedResponse.data.data || relatedResponse.data || [])
              .filter(p => p.id !== productData.id).slice(0, 4)
          );
        } catch {}
      }

      if (productData.publisherId) {
        try {
          const sellerResponse = await productService.getProductsByCreator(productData.publisherId);
          setSellerProducts(
            (sellerResponse.data.data || sellerResponse.data || [])
              .filter(p => p.id !== productData.id).slice(0, 3)
          );
        } catch {}
      }

      setError(null);
    } catch {
      setError(t('No se pudieron cargar los detalles del producto'));
    } finally {
      setLoading(false);
    }
  };

  const handleContactSeller = () => {
    if (!user) { navigate('/login', { state: { from: `/products/${id}` } }); return; }
    // El chat es user-to-user; pasamos el productId como referencia para
    // que /messages muestre un chip "adjunto" arriba del input y termine
    // embebido en el primer mensaje como tarjeta clickeable.
    navigate(buildGoMessagesPath({ userId: product?.publisherId, productId: product?.id || id }));
  };

  const handleWishlistToggle = async () => {
    if (!user) { navigate('/login', { state: { from: `/products/${id}` } }); return; }
    try {
      if (isWishlisted) {
        await wishlistService.removeFromWishlist(id);
        setIsWishlisted(false);
        showSnackbar(t('Eliminado de favoritos'));
      } else {
        await wishlistService.addToWishlist(id);
        setIsWishlisted(true);
        showSnackbar(t('Anadido a favoritos'));
      }
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      queryClient.invalidateQueries({ queryKey: ['wishlist-status'] });
      queryClient.invalidateQueries({ queryKey: ['wishlist-cursor'] });
    } catch (err) {
      showSnackbar(err.response?.data?.message || t('Error al actualizar favoritos'), 'error');
    }
  };

  const handleReservation = async () => {
    if (!user) { navigate('/login', { state: { from: `/products/${id}` } }); return; }
    setSubmitting(true);
    try {
      const res = await reservationService.createReservation(id, null);
      const reservation = res?.data?.data || res?.data;
      showSnackbar(t('Solicitud de reserva enviada correctamente!'));
      // Refresca stock / status — la reserva puede o no dejar el producto en
      // RESERVED según si quedaba stock. Si el refetch falla, fallback local.
      try {
        const refreshed = await productService.getProductById(id);
        setProduct(refreshed.data?.data || refreshed.data);
      } catch {
        setProduct(prev => ({ ...prev, status: 'RESERVED' }));
      }
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['productCards'] });
      if (reservation?.id) {
        setReservedId(reservation.id);
      } else {
        setReservationDialog(false);
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || t('No se pudo crear la reserva'), 'error');
    } finally { setSubmitting(false); }
  };

  const closeReservationDialog = () => {
    setReservationDialog(false);
    setReservedId(null);
  };

  const openChatAboutReservation = () => {
    if (!reservedId) return;
    navigate(buildGoMessagesPath({
      userId: product?.publisherId,
      reservationId: reservedId,
    }));
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await productService.deleteProduct(id);
      showSnackbar(t('Producto eliminado correctamente'));
      navigate('/');
    } catch { showSnackbar(t('No se pudo eliminar el producto'), 'error'); }
    finally { setSubmitting(false); setDeleteDialog(false); }
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: product.name, text: product.description, url });
    } else {
      navigator.clipboard.writeText(url);
      showSnackbar(t('Enlace copiado al portapapeles!'));
    }
  };

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const handleKickSeller = async () => {
    const ok = await kickFromCommunity(product.communityId, product.publisherId);
    setKickDialog(false);
    if (ok) showSnackbar(t('Usuario expulsado de la comunidad'));
  };

  const handleBanSeller = async () => {
    const ok = await banFromCommunity(product.communityId, product.publisherId, banReason || null);
    setBanDialog(false);
    setBanReason('');
    if (ok) showSnackbar(t('Usuario bloqueado de la comunidad'));
  };

  // Build images array
  const getImageUrl = (imageId) => imageId ? `/api/images/${imageId}` : (product?.imageId ? `/api/images/${product.imageId}` : null);
  const images = product
    ? (productImages.length > 0
      ? productImages.sort((a, b) => a.orderIndex - b.orderIndex).map(img => ({ url: `/api/images/${img.imageId}`, alt: product.name, id: img.imageId }))
      : [{ url: getImageUrl(product.imageId), alt: product.name, id: product.imageId }])
    : [];

  const handlePreviousImage = () => setSelectedImage(prev => (prev === 0 ? images.length - 1 : prev - 1));
  const handleNextImage = () => setSelectedImage(prev => (prev === images.length - 1 ? 0 : prev + 1));

  // Shared computed values
  const isOwner = !loading && product ? (user && product.publisherId === user.id) : false;
  const canReserve = !loading && product ? (!isOwner && product.status === 'AVAILABLE') : false;
  const isCommunityAdmin = !isOwner && product && Array.isArray(moderationContext)
    ? moderationContext.some((c) => c.id === product.communityId)
    : false;
  const statusCfgTW = product ? (STATUS_CONFIG_TW[product.status] || STATUS_CONFIG_TW.AVAILABLE) : STATUS_CONFIG_TW.AVAILABLE;
  const statusCfgMUI = product ? (STATUS_CONFIG_MUI[product.status] || STATUS_CONFIG_MUI.AVAILABLE) : STATUS_CONFIG_MUI.AVAILABLE;

  const VariantSelector = null;

  // ─── Shared Dialogs ─────────────────────────────────────────────────
  // IMPORTANT: SharedDialogs debe ser JSX (no un componente declarado dentro
  // del render). Si fuera componente, cada render crearía un tipo nuevo y
  // React desmontaría/remontaría todos los Dialog/Snackbar (portales). Cuando
  // handleReservation dispara varios setState en cadena, eso deja portales
  // apilados a medio cerrar — se ve como "la misma página puesta muchas veces".
  const sharedDialogs = (
    <>
      {/* Reservation Dialog — modal custom (tailwind + theme tokens) en vez
          del MUI Dialog, para matchear el resto de la app (TradeAcceptedModal
          y compañía). Dos estados: confirmar reserva, o éxito con acciones
          de chat / ver compraventas (sin email/WhatsApp). */}
      {reservationDialog && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeReservationDialog}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-md bg-surface rounded-2xl shadow-2xl overflow-hidden"
          >
            {!reservedId ? (
              <>
                <div className="px-5 py-4 border-b border-outline-variant flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-xl">shopping_cart</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-black text-on-surface">{t('Reservar producto')}</p>
                    <p className="text-xs text-on-surface-variant">{t('Confirmá la solicitud')}</p>
                  </div>
                </div>
                <div className="px-5 py-4">
                  <p className="text-sm text-on-surface-variant mb-3">
                    {t('Estás por reservar:')}
                  </p>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-container">
                    {product?.imageId ? (
                      <img
                        src={getImageUrl(product.imageId)}
                        alt=""
                        className="w-12 h-12 rounded-lg object-cover bg-surface-container-highest shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-surface-container-highest flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-on-surface-variant">shopping_bag</span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-on-surface truncate">{product?.name}</p>
                      {product?.price != null && (
                        <p className="text-xs font-semibold text-primary">
                          {formatCurrency(product.price)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-outline-variant flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeReservationDialog}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-on-surface-variant hover:bg-surface-container transition-colors"
                  >
                    {t('Cancelar')}
                  </button>
                  <button
                    type="button"
                    onClick={handleReservation}
                    disabled={submitting}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-bold bg-primary text-on-primary hover:opacity-90 transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                  >
                    {submitting && (
                      <span className="w-3.5 h-3.5 border-2 border-on-primary border-t-transparent rounded-full animate-spin" />
                    )}
                    {submitting ? t('Enviando...') : t('Confirmar reserva')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="px-5 py-4 border-b border-outline-variant flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-black text-on-surface">{t('Reserva enviada')}</p>
                    <p className="text-xs text-on-surface-variant">{t('Confirmamos tu solicitud')}</p>
                  </div>
                </div>
                <div className="px-5 py-4">
                  <p className="text-sm text-on-surface-variant">
                    {t('Tu reserva fue enviada al vendedor. Podés escribirle para coordinar o verla en tus compraventas.')}
                  </p>
                </div>
                <div className="px-5 py-3 border-t border-outline-variant flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={openChatAboutReservation}
                    className="w-full px-3 py-2.5 rounded-lg text-sm font-bold bg-primary text-on-primary hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-1.5"
                  >
                    <span className="material-symbols-outlined text-base">forum</span>
                    {t('Escribirle al vendedor')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { closeReservationDialog(); navigate('/reservations'); }}
                    className="w-full px-3 py-2.5 rounded-lg text-sm font-bold border border-outline text-on-surface hover:bg-surface-container transition-colors inline-flex items-center justify-center gap-1.5"
                  >
                    <span className="material-symbols-outlined text-base">swap_horiz</span>
                    {t('Ver mis compraventas')}
                  </button>
                  <button
                    type="button"
                    onClick={closeReservationDialog}
                    className="w-full px-3 py-2 rounded-lg text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-colors"
                  >
                    {t('Cerrar')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>{t('Eliminar producto')}</DialogTitle>
        <DialogContent>
          <p>{t('Seguro que quieres eliminar este producto? Esta accion no se puede deshacer.')}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>{t('Cancelar')}</Button>
          <Button onClick={handleDelete} color="error" disabled={submitting}>
            {submitting ? t('Eliminando...') : t('Eliminar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kick Dialog */}
      <Dialog open={kickDialog} onClose={() => setKickDialog(false)}>
        <DialogTitle>{t('Expulsar al vendedor de la comunidad')}</DialogTitle>
        <DialogContent>
          <p>{t('Are you sure you want to remove {{name}}?', { name: product?.publisherName || '' })}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKickDialog(false)}>{t('Cancelar')}</Button>
          <Button onClick={handleKickSeller} color="warning" variant="contained">
            {t('Kick from community')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog open={banDialog} onClose={() => setBanDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('Bloquear al vendedor de la comunidad')}</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 12 }}>{t('Are you sure you want to ban {{name}}?', { name: product?.publisherName || '' })}</p>
          <TextField
            autoFocus
            margin="dense"
            label={t('Reason (optional)')}
            fullWidth
            variant="outlined"
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBanDialog(false); setBanReason(''); }}>{t('Cancelar')}</Button>
          <Button onClick={handleBanSeller} color="error" variant="contained">
            {t('Ban from community')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Lightbox */}
      {product && (
        <SimpleImageViewer
          images={images}
          open={lightboxOpen}
          currentIndex={selectedImage}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );


  /* ═══════════════════════════════════════════════════════════════════════
     MUI VARIANTS — Parameterized renderer
     cardStyle: 'border' | 'shadow' | 'gray'
     sidebar: boolean
     ═══════════════════════════════════════════════════════════════════════ */
  const renderMUIVariant = ({ cardStyle = 'border', sidebar = false } = {}) => {
    const theme = muiTheme;
    const statusCfg = statusCfgMUI;

    // Card styles per variant
    const cardSxMap = {
      border: {
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.5),
        overflow: 'hidden',
      },
      shadow: {
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      },
      gray: {
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      },
    };
    const cardSx = cardSxMap[cardStyle] || cardSxMap.border;

    // Info panel card style (with padding)
    const infoPanelCardSxMap = {
      border: {
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.5),
        p: 2.5,
      },
      shadow: {
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        p: 2.5,
      },
      gray: {
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        p: 2.5,
      },
    };
    const infoPanelCardSx = infoPanelCardSxMap[cardStyle] || infoPanelCardSxMap.border;

    // Related product card style
    const relatedCardSxMap = {
      border: {
        bgcolor: 'background.paper', borderRadius: 2.5, overflow: 'hidden',
        cursor: 'pointer', border: '1px solid', borderColor: alpha(theme.palette.divider, 0.4),
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'scale(1.02)', boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.08)}` },
      },
      shadow: {
        bgcolor: 'background.paper', borderRadius: 2.5, overflow: 'hidden',
        cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'scale(1.02)', boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.08)}` },
      },
      gray: {
        bgcolor: 'background.paper', borderRadius: 2.5, overflow: 'hidden',
        cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': { transform: 'scale(1.02)', boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.08)}` },
      },
    };
    const relatedCardSx = relatedCardSxMap[cardStyle] || relatedCardSxMap.border;

    // Page background
    const pageBgColor = cardStyle === 'gray' ? '#f5f5f5' : undefined;

    // Shared info panel props
    const infoPanelProps = {
      product, community, isOwner, canReserve, statusCfg, isWishlisted, sellerProducts,
      isCommunityAdmin,
      onReserve: () => setReservationDialog(true),
      onContact: handleContactSeller,
      onEdit: () => navigate(`/products/${id}/edit`),
      onDelete: () => setDeleteDialog(true),
      onKick: () => setKickDialog(true),
      onBan: () => setBanDialog(true),
      navigate, t, theme,
      cardSxOverride: infoPanelCardSx,
    };

    // Loading
    const loadingContent = (
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 4 }}>
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 3 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 4 }}>
          <Box>
            <Skeleton variant="rounded" height={500} sx={{ borderRadius: 3 }} />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="rounded" width={80} height={80} sx={{ borderRadius: 1.5 }} />
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={52} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={52} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
          </Box>
        </Box>
      </Box>
    );

    // Error
    const errorContent = (
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 4, textAlign: 'center', mt: 8 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {error || t('Producto no encontrado')}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('Volver al inicio')}
        </Button>
      </Box>
    );

    // Main content (function to avoid evaluating when product is null)
    const mainContent = () => (
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2, md: 4 } }}>

        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNext sx={{ fontSize: 18 }} />}
          sx={{ mb: 3, '& .MuiBreadcrumbs-li': { fontSize: 14, fontWeight: 500 } }}
        >
          <Typography
            component={Link}
            to="/"
            sx={{ textDecoration: 'none', color: 'text.secondary', '&:hover': { color: 'text.primary' }, fontSize: 14, fontWeight: 500 }}
          >
            {t('Inicio')}
          </Typography>
          {community && (
            <Typography
              component={Link}
              to={`/communities/${community.id}`}
              sx={{ textDecoration: 'none', color: 'text.secondary', '&:hover': { color: 'text.primary' }, fontSize: 14, fontWeight: 500 }}
            >
              {community.name}
            </Typography>
          )}
          <Typography sx={{ color: 'text.primary', fontSize: 14, fontWeight: 700, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product.name}
          </Typography>
        </Breadcrumbs>

        {/* Two-column layout */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: { xs: 3, md: 4 } }}>

          {/* LEFT COLUMN */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* Image Card */}
            <Box sx={cardSx}>
              <Box sx={{ position: 'relative', p: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    position: 'relative',
                    cursor: 'zoom-in',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : '#fafafa',
                    '&:hover .image-nav-btn': { opacity: 1 },
                  }}
                  onClick={(e) => {
                    if (!e.target.closest('.nav-btn-area')) setLightboxOpen(true);
                  }}
                >
                  {images[selectedImage]?.url ? (
                    <Box
                      component="img"
                      src={images[selectedImage].url}
                      alt={images[selectedImage].alt}
                      sx={{ width: '100%', height: { xs: 350, sm: 450, md: 500 }, objectFit: 'contain', display: 'block' }}
                    />
                  ) : (
                    <Box sx={{ width: '100%', height: { xs: 350, sm: 450, md: 500 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="h2" color="text.disabled">No image</Typography>
                    </Box>
                  )}

                  {/* Navigation arrows */}
                  {images.length > 1 && (
                    <>
                      <IconButton
                        className="nav-btn-area image-nav-btn"
                        onClick={(e) => { e.stopPropagation(); handlePreviousImage(); }}
                        sx={{
                          position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                          bgcolor: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(8px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          opacity: 0, transition: 'opacity 0.2s',
                          '&:hover': { bgcolor: theme.palette.background.paper },
                        }}
                        size="small"
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        className="nav-btn-area image-nav-btn"
                        onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                        sx={{
                          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                          bgcolor: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(8px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          opacity: 0, transition: 'opacity 0.2s',
                          '&:hover': { bgcolor: theme.palette.background.paper },
                        }}
                        size="small"
                      >
                        <ChevronRight />
                      </IconButton>
                      <Box sx={{
                        position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                        px: 2, py: 0.5, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff',
                        borderRadius: 10, fontSize: 13, fontWeight: 600,
                      }}>
                        {selectedImage + 1} / {images.length}
                      </Box>
                    </>
                  )}
                </Box>

                {/* Status badge */}
                <Box sx={{ position: 'absolute', top: { xs: 16, sm: 20 }, left: { xs: 16, sm: 20 } }}>
                  <Chip
                    label={t(statusCfg.label)}
                    color={statusCfg.color}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 12, height: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}
                  />
                </Box>

                {/* Action buttons */}
                <Box sx={{ position: 'absolute', top: { xs: 16, sm: 20 }, right: { xs: 16, sm: 20 }, display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(8px)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                      '&:hover': { bgcolor: theme.palette.background.paper },
                    }}
                  >
                    <Fullscreen fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); handleWishlistToggle(); }}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(8px)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                      '&:hover': { bgcolor: theme.palette.background.paper },
                    }}
                  >
                    {isWishlisted ? <Favorite sx={{ color: 'error.main' }} fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                  </IconButton>
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); handleShare(); }}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(8px)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                      '&:hover': { bgcolor: theme.palette.background.paper },
                    }}
                  >
                    <Share fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Thumbnails */}
              {images.length > 1 && (
                <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{
                    display: 'flex', gap: 1.5, overflowX: 'auto', pt: 1,
                    '&::-webkit-scrollbar': { height: 0 },
                  }}>
                    {images.map((img, index) => (
                      <Box
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        sx={{
                          flexShrink: 0, width: 76, height: 76, borderRadius: 1.5, overflow: 'hidden',
                          cursor: 'pointer', border: '2px solid',
                          borderColor: selectedImage === index ? 'primary.main' : 'transparent',
                          opacity: selectedImage === index ? 1 : 0.6,
                          transition: 'all 0.2s',
                          '&:hover': { opacity: 1 },
                        }}
                      >
                        <Box component="img" src={img.url} alt={img.alt} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Divider */}
              <Box sx={{ mx: 2, borderTop: '1px solid', borderColor: alpha(theme.palette.divider, 0.4) }} />

              {/* Product title + price */}
              <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2.5, pb: 1 }}>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.3, mb: 1.5 }}>
                  {product.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h5" fontWeight={700} color="primary">
                    {formatCurrency(product.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </Typography>
                  {product.condition && (
                    <Chip
                      label={t(CONDITION_LABELS[product.condition] || product.condition)}
                      size="small"
                      sx={{
                        fontWeight: 700, fontSize: 11, height: 24,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                        textTransform: 'uppercase',
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Visibility sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      {formatNumber(product.viewCount || 0)} {t('visitas')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Description */}
              {product.description && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.5, pb: 2.5 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {product.description}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Info Panel - Mobile only */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <ProductInfoPanelV1 {...infoPanelProps} />
            </Box>

            {/* Related Products */}
            {relatedProducts.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  {t('Mas de esta comunidad')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                  {relatedProducts.map(rp => (
                    <Box
                      key={rp.id}
                      onClick={() => navigate(`/products/${rp.id}`)}
                      sx={relatedCardSx}
                    >
                      <Box sx={{ aspectRatio: '1/1', overflow: 'hidden' }}>
                        {rp.imageId ? (
                          <Box component="img" src={`/api/images/${rp.imageId}`} alt={rp.name} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.divider, 0.15) }}>
                            <Typography color="text.disabled">No image</Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="caption" fontWeight={700} noWrap sx={{ display: 'block' }}>
                          {rp.name}
                        </Typography>
                        <Typography variant="caption" fontWeight={700} color="primary" sx={{ mt: 0.5, display: 'block' }}>
                          {formatCurrency(rp.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* RIGHT COLUMN — desktop */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 24, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <ProductInfoPanelV1 {...infoPanelProps} />
            </Box>
          </Box>
        </Box>
      </Box>
    );

    // Pick content based on state
    const content = loading ? loadingContent : (error || !product) ? errorContent : mainContent();

    // Wrap in page background for gray variant
    const withBg = pageBgColor
      ? <Box sx={{ bgcolor: pageBgColor, minHeight: '100vh' }}>{content}</Box>
      : content;

    // Wrap in sidebar shell if needed
    if (sidebar) {
      return (
        <div className="dashboard-new font-body h-[100dvh] overflow-hidden flex" style={{ ...themeVars, backgroundColor: 'var(--chrome-bg)' }}>
          <DashboardSidebar activePath="" />
          <div className="flex-1 overflow-y-auto hide-scrollbar">
            {withBg}
            {VariantSelector}
          </div>
        </div>
      );
    }

    return (
      <>
        {withBg}
        {VariantSelector}
      </>
    );
  };


  /* ═══════════════════════════════════════════════════════════════════════
     VARIANT 6 — "TW Con Header" (Tailwind, original with header)
     ═══════════════════════════════════════════════════════════════════════ */
  const renderTWConHeader = () => {
    const theme = themeVars;

    // ─── Shared page shell ─────────────────────────────────────────
    const PageShell = ({ children }) => (
      <div
        className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col"
        style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
      >
        {/* Top bar */}
        <header
          className="flex items-center gap-3 px-4 tablet-sm:px-8 py-3 shrink-0 z-50"
          style={{ backgroundColor: 'var(--chrome-sidebar-bg)', borderBottom: '1px solid var(--chrome-border-subtle)' }}
        >
          <a
            onClick={() => setNavOpen(true)}
            className="w-9 h-9 rounded-lg flex items-center justify-center cursor-pointer hover:bg-surface-container transition-colors"
            style={{ color: 'var(--chrome-muted)' }}
          >
            <span className="material-symbols-outlined">menu</span>
          </a>
          <a onClick={() => navigate('/')} className="text-lg font-bold tracking-tight cursor-pointer" style={{ color: 'var(--chrome-strong)' }}>
            VAT<span className="text-primary">UCO</span>
          </a>
        </header>

        {/* Nav overlay */}
        {navOpen && (
          <div className="fixed inset-0 z-[70] flex">
            <div className="absolute inset-0 bg-black/20" onClick={() => setNavOpen(false)} />
            <div className="relative w-72 max-w-[80vw] h-full flex flex-col py-8 px-6 shadow-2xl overflow-y-auto" style={{ backgroundColor: 'var(--chrome-sidebar-bg)' }}>
              <div className="flex items-center justify-between mb-10">
                <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--chrome-strong)' }}>VAT<span className="text-primary">UCO</span></h1>
                <a onClick={() => setNavOpen(false)} className="cursor-pointer" style={{ color: 'var(--chrome-muted)' }}>
                  <span className="material-symbols-outlined">close</span>
                </a>
              </div>
              <nav className="flex-1 space-y-2">
                {NAV_ITEMS.map(({ path, icon, label }) => (
                  <a
                    key={path}
                    onClick={() => { setNavOpen(false); navigate(path); }}
                    className="flex items-center gap-3 py-3 px-4 rounded-xl transition-colors duration-200 cursor-pointer"
                    style={{ color: 'var(--chrome-muted)' }}
                  >
                    <span className="material-symbols-outlined">{icon}</span>
                    <span>{t(label)}</span>
                  </a>
                ))}
              </nav>
              <a
                onClick={() => { setNavOpen(false); navigate('/create-product'); }}
                className="mt-6 w-full py-3 bg-gradient-to-r from-primary to-primary-container text-white font-semibold rounded-xl flex items-center justify-center gap-2 cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">add_circle</span>
                New Entry
              </a>
            </div>
          </div>
        )}

        {children}
      </div>
    );

    const statusCfg = statusCfgTW;

    // Loading skeleton
    if (loading) {
      return (
        <PageShell>
          <main className="flex-1 overflow-y-auto px-4 tablet-sm:px-8 py-8 hide-scrollbar">
            <div className="max-w-6xl mx-auto">
              <div className="h-5 bg-surface-container-high rounded animate-pulse w-48 mb-6" />
              <div className="grid grid-cols-1 tablet-md:grid-cols-3 gap-8">
                <div className="tablet-md:col-span-2 space-y-4">
                  <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/30 p-4" style={{ height: '520px' }}>
                    <div className="w-full h-full bg-surface-container-high rounded-xl animate-pulse" />
                  </div>
                  <div className="flex gap-2">
                    {[1,2,3].map(i => <div key={i} className="w-20 h-20 bg-surface-container-high rounded-lg animate-pulse" />)}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="h-10 bg-surface-container-high rounded-xl animate-pulse" />
                  <div className="h-12 bg-surface-container-high rounded-xl animate-pulse" />
                  <div className="h-12 bg-surface-container-high rounded-xl animate-pulse" />
                </div>
              </div>
            </div>
          </main>
          {VariantSelector}
        </PageShell>
      );
    }

    // Error state
    if (error || !product) {
      return (
        <PageShell>
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <span className="material-symbols-outlined text-6xl text-outline-variant mb-4 block">error_outline</span>
              <h2 className="text-lg font-bold text-on-surface mb-2">{error || t('Producto no encontrado')}</h2>
              <a onClick={() => navigate('/')} className="text-primary font-semibold cursor-pointer hover:underline">
                {t('Volver al inicio')}
              </a>
            </div>
          </main>
          {VariantSelector}
        </PageShell>
      );
    }

    // Main render
    return (
      <PageShell>
        <main className="flex-1 overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
          <div className="max-w-6xl mx-auto px-4 tablet-sm:px-8 py-6">

            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm mb-6">
              <a onClick={() => navigate('/')} className="text-on-surface-variant hover:text-on-surface cursor-pointer transition-colors font-medium">
                {t('Inicio')}
              </a>
              {community && (
                <>
                  <span className="material-symbols-outlined text-[16px] text-outline-variant">chevron_right</span>
                  <a onClick={() => navigate(`/communities/${community.id}`)} className="text-on-surface-variant hover:text-on-surface cursor-pointer transition-colors font-medium">
                    {community.name}
                  </a>
                </>
              )}
              <span className="material-symbols-outlined text-[16px] text-outline-variant">chevron_right</span>
              <span className="text-on-surface font-bold truncate max-w-[300px]">{product.name}</span>
            </nav>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 tablet-md:grid-cols-3 gap-8">

              {/* LEFT COLUMN */}
              <div className="tablet-md:col-span-2 space-y-6">

                {/* Image Card */}
                <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/30 overflow-hidden">
                  <div className="relative group px-3 pt-14 pb-3 tablet-sm:px-4 tablet-sm:pt-16 tablet-sm:pb-4">
                    <div
                      className="relative cursor-zoom-in rounded-xl overflow-hidden"
                      onClick={(e) => { if (!e.target.closest('.nav-btn')) setLightboxOpen(true); }}
                    >
                      {images[selectedImage]?.url ? (
                        <img
                          src={images[selectedImage].url}
                          alt={images[selectedImage].alt}
                          className="w-full object-contain"
                          style={{ height: '500px' }}
                        />
                      ) : (
                        <div className="w-full flex items-center justify-center bg-surface-container-high" style={{ height: '500px' }}>
                          <span className="material-symbols-outlined text-7xl text-outline-variant">image</span>
                        </div>
                      )}

                      {/* Navigation arrows */}
                      {images.length > 1 && (
                        <>
                          <a
                            className="nav-btn absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); handlePreviousImage(); }}
                          >
                            <span className="material-symbols-outlined text-on-surface">chevron_left</span>
                          </a>
                          <a
                            className="nav-btn absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                          >
                            <span className="material-symbols-outlined text-on-surface">chevron_right</span>
                          </a>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 text-white text-sm rounded-full font-medium">
                            {selectedImage + 1} / {images.length}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Status badge */}
                    <div className="absolute top-3 tablet-sm:top-4 left-3 tablet-sm:left-4 flex items-center gap-2">
                      <a
                        onClick={(e) => { e.stopPropagation(); navigate(-1); }}
                        className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors"
                        title={t('Volver')}
                        aria-label={t('Volver')}
                      >
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">arrow_back</span>
                      </a>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm ${statusCfg.classes}`}>
                        <span className="material-symbols-outlined text-[14px]">{statusCfg.icon}</span>
                        {t(statusCfg.label)}
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div className="absolute top-3 tablet-sm:top-4 right-3 tablet-sm:right-4 flex gap-2">
                      <a
                        onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
                        className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors"
                      >
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">fullscreen</span>
                      </a>
                      <a
                        onClick={(e) => { e.stopPropagation(); handleWishlistToggle(); }}
                        className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors"
                      >
                        <span className="material-symbols-outlined text-[20px]" style={{ color: isWishlisted ? '#ef4444' : undefined, fontVariationSettings: isWishlisted ? "'FILL' 1" : undefined }}>
                          favorite
                        </span>
                      </a>
                      <a
                        onClick={(e) => { e.stopPropagation(); handleShare(); }}
                        className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors"
                      >
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">share</span>
                      </a>
                    </div>
                  </div>

                  {/* Thumbnails */}
                  {images.length > 1 && (
                    <div className="px-3 tablet-sm:px-4 pb-3 tablet-sm:pb-4">
                      <div className="flex gap-2.5 overflow-x-auto hide-scrollbar pt-2">
                        {images.map((img, index) => (
                          <a
                            key={index}
                            onClick={() => setSelectedImage(index)}
                            className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${selectedImage === index ? 'border-primary shadow-sm' : 'border-transparent opacity-60 hover:opacity-100'}`}
                          >
                            <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="mx-3 tablet-sm:mx-4 border-t border-outline-variant/30" />

                  {/* Product title, price, condition */}
                  <div className="px-4 tablet-sm:px-5 pt-5 pb-2">
                    <h1 className="text-2xl font-bold text-on-surface leading-tight mb-2">{product.name}</h1>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-2xl font-bold text-primary">
                        {formatCurrency(product.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                      {product.condition && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">
                          {t(CONDITION_LABELS[product.condition] || product.condition)}
                        </span>
                      )}
                      {product.stock == null ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary/10 text-secondary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">all_inclusive</span>
                          {t('Stock ilimitado')}
                        </span>
                      ) : product.stock === 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-error/10 text-error text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Sin stock')}
                        </span>
                      ) : product.stock === 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Queda 1 unidad')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Quedan {{count}} unidades', { count: product.stock })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {product.description && (
                    <div className="px-4 tablet-sm:px-5 pt-3 pb-5">
                      <p className="text-base text-on-surface-variant leading-relaxed whitespace-pre-wrap">{product.description}</p>
                    </div>
                  )}
                </div>

                {/* Product Info — mobile only */}
                <div className="tablet-md:hidden">
                  <ProductInfoPanelV0
                    product={product}
                    community={community}
                    isOwner={isOwner}
                    canReserve={canReserve}
                    statusCfg={statusCfg}
                    isWishlisted={isWishlisted}
                    sellerProducts={sellerProducts}
                    isCommunityAdmin={isCommunityAdmin}
                    onReserve={() => setReservationDialog(true)}
                    onContact={handleContactSeller}
                    onEdit={() => navigate(`/products/${id}/edit`)}
                    onDelete={() => setDeleteDialog(true)}
                    onKick={() => setKickDialog(true)}
                    onBan={() => setBanDialog(true)}
                    navigate={navigate}
                    t={t}
                    hideHeaderCard
                  />
                </div>

                {/* Related Products */}
                {relatedProducts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-on-surface mb-4">{t('Mas de esta comunidad')}</h3>
                    <div className="grid grid-cols-2 mobile-md:grid-cols-4 gap-3">
                      {relatedProducts.map(rp => (
                        <a
                          key={rp.id}
                          onClick={() => navigate(`/products/${rp.id}`)}
                          className="bg-surface-container rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform shadow-sm"
                        >
                          <div className="aspect-square overflow-hidden" style={{ aspectRatio: '1/1' }}>
                            {rp.imageId ? (
                              <img src={`/api/images/${rp.imageId}`} alt={rp.name} className="w-full h-full object-contain" />
                            ) : (
                              <div className="w-full h-full bg-surface-container-high flex items-center justify-center">
                                <span className="material-symbols-outlined text-3xl text-outline-variant">image</span>
                              </div>
                            )}
                          </div>
                          <div className="p-2.5">
                            <p className="text-xs font-bold text-on-surface truncate">{rp.name}</p>
                            <p className="text-xs font-bold text-primary mt-0.5">
                              {formatCurrency(rp.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN — desktop only */}
              <div className="hidden tablet-md:block">
                <div className="sticky top-8 space-y-5">
                  <ProductInfoPanelV0
                    product={product}
                    community={community}
                    isOwner={isOwner}
                    canReserve={canReserve}
                    statusCfg={statusCfg}
                    isWishlisted={isWishlisted}
                    sellerProducts={sellerProducts}
                    isCommunityAdmin={isCommunityAdmin}
                    onReserve={() => setReservationDialog(true)}
                    onContact={handleContactSeller}
                    onEdit={() => navigate(`/products/${id}/edit`)}
                    onDelete={() => setDeleteDialog(true)}
                    onKick={() => setKickDialog(true)}
                    onBan={() => setBanDialog(true)}
                    navigate={navigate}
                    t={t}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
        {VariantSelector}
      </PageShell>
    );
  };


  /* ═══════════════════════════════════════════════════════════════════════
     VARIANT 7 — "TW Sin Header" (same as TW Con Header but no header/nav overlay)
     ═══════════════════════════════════════════════════════════════════════ */
  const renderTWSinHeader = () => {
    const theme = themeVars;
    const statusCfg = statusCfgTW;

    const Shell = ({ children }) => (
      <div
        className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col"
        style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
      >
        {children}
      </div>
    );

    if (loading) {
      return (
        <Shell>
          <main className="flex-1 overflow-y-auto px-4 tablet-sm:px-8 py-8 hide-scrollbar">
            <div className="max-w-6xl mx-auto">
              <div className="h-5 bg-surface-container-high rounded animate-pulse w-48 mb-6" />
              <div className="grid grid-cols-1 tablet-md:grid-cols-3 gap-8">
                <div className="tablet-md:col-span-2 space-y-4">
                  <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/30 p-4" style={{ height: '520px' }}>
                    <div className="w-full h-full bg-surface-container-high rounded-xl animate-pulse" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="h-10 bg-surface-container-high rounded-xl animate-pulse" />
                  <div className="h-12 bg-surface-container-high rounded-xl animate-pulse" />
                </div>
              </div>
            </div>
          </main>
          {VariantSelector}
        </Shell>
      );
    }

    if (error || !product) {
      return (
        <Shell>
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <span className="material-symbols-outlined text-6xl text-outline-variant mb-4 block">error_outline</span>
              <h2 className="text-lg font-bold text-on-surface mb-2">{error || t('Producto no encontrado')}</h2>
              <a onClick={() => navigate('/')} className="text-primary font-semibold cursor-pointer hover:underline">{t('Volver al inicio')}</a>
            </div>
          </main>
          {VariantSelector}
        </Shell>
      );
    }

    return (
      <Shell>
        <main className="flex-1 overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
          <div className="max-w-6xl mx-auto px-4 tablet-sm:px-8 py-6">
            {/* Two-column layout */}
            <div className="grid grid-cols-1 tablet-md:grid-cols-3 gap-8">
              {/* LEFT COLUMN */}
              <div className="tablet-md:col-span-2 space-y-6">
                {/* Image Card */}
                <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/30 overflow-hidden">
                  <div className="relative group px-3 pt-14 pb-3 tablet-sm:px-4 tablet-sm:pt-16 tablet-sm:pb-4">
                    <div className="relative cursor-zoom-in rounded-xl overflow-hidden" onClick={(e) => { if (!e.target.closest('.nav-btn')) setLightboxOpen(true); }}>
                      {images[selectedImage]?.url ? (
                        <img src={images[selectedImage].url} alt={images[selectedImage].alt} className="w-full object-contain" style={{ height: '500px' }} />
                      ) : (
                        <div className="w-full flex items-center justify-center bg-surface-container-high" style={{ height: '500px' }}>
                          <span className="material-symbols-outlined text-7xl text-outline-variant">image</span>
                        </div>
                      )}
                      {images.length > 1 && (
                        <>
                          <a className="nav-btn absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={(e) => { e.stopPropagation(); handlePreviousImage(); }}>
                            <span className="material-symbols-outlined text-on-surface">chevron_left</span>
                          </a>
                          <a className="nav-btn absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={(e) => { e.stopPropagation(); handleNextImage(); }}>
                            <span className="material-symbols-outlined text-on-surface">chevron_right</span>
                          </a>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 text-white text-sm rounded-full font-medium">{selectedImage + 1} / {images.length}</div>
                        </>
                      )}
                    </div>
                    <div className="absolute top-3 tablet-sm:top-4 left-3 tablet-sm:left-4 flex items-center gap-2">
                      <a
                        onClick={(e) => { e.stopPropagation(); navigate(-1); }}
                        className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors"
                        title={t('Volver')}
                        aria-label={t('Volver')}
                      >
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">arrow_back</span>
                      </a>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm ${statusCfg.classes}`}>
                        <span className="material-symbols-outlined text-[14px]">{statusCfg.icon}</span>
                        {t(statusCfg.label)}
                      </span>
                    </div>
                    <div className="absolute top-3 tablet-sm:top-4 right-3 tablet-sm:right-4 flex gap-2">
                      <a onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors">
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">fullscreen</span>
                      </a>
                      <a onClick={(e) => { e.stopPropagation(); handleWishlistToggle(); }} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors">
                        <span className="material-symbols-outlined text-[20px]" style={{ color: isWishlisted ? '#ef4444' : undefined, fontVariationSettings: isWishlisted ? "'FILL' 1" : undefined }}>favorite</span>
                      </a>
                      <a onClick={(e) => { e.stopPropagation(); handleShare(); }} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors">
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">share</span>
                      </a>
                    </div>
                  </div>
                  {images.length > 1 && (
                    <div className="px-3 tablet-sm:px-4 pb-3 tablet-sm:pb-4">
                      <div className="flex gap-2.5 overflow-x-auto hide-scrollbar pt-2">
                        {images.map((img, index) => (
                          <a key={index} onClick={() => setSelectedImage(index)} className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${selectedImage === index ? 'border-primary shadow-sm' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                            <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mx-3 tablet-sm:mx-4 border-t border-outline-variant/30" />
                  <div className="px-4 tablet-sm:px-5 pt-5 pb-2">
                    <h1 className="text-2xl font-bold text-on-surface leading-tight mb-2">{product.name}</h1>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-2xl font-bold text-primary">{formatCurrency(product.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                      {product.condition && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">{t(CONDITION_LABELS[product.condition] || product.condition)}</span>
                      )}
                      {product.stock == null ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary/10 text-secondary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">all_inclusive</span>
                          {t('Stock ilimitado')}
                        </span>
                      ) : product.stock === 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-error/10 text-error text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Sin stock')}
                        </span>
                      ) : product.stock === 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Queda 1 unidad')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Quedan {{count}} unidades', { count: product.stock })}
                        </span>
                      )}
                    </div>
                  </div>
                  {product.description && (
                    <div className="px-4 tablet-sm:px-5 pt-3 pb-5">
                      <p className="text-base text-on-surface-variant leading-relaxed whitespace-pre-wrap">{product.description}</p>
                    </div>
                  )}
                </div>
                {/* Mobile info panel */}
                <div className="tablet-md:hidden">
                  <ProductInfoPanelV0 product={product} community={community} isOwner={isOwner} canReserve={canReserve} statusCfg={statusCfg} isWishlisted={isWishlisted} sellerProducts={sellerProducts} isCommunityAdmin={isCommunityAdmin} onReserve={() => setReservationDialog(true)} onContact={handleContactSeller} onEdit={() => navigate(`/products/${id}/edit`)} onDelete={() => setDeleteDialog(true)} onKick={() => setKickDialog(true)} onBan={() => setBanDialog(true)} navigate={navigate} t={t} hideHeaderCard />
                </div>
                {/* Related Products */}
                {relatedProducts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-on-surface mb-4">{t('Mas de esta comunidad')}</h3>
                    <div className="grid grid-cols-2 mobile-md:grid-cols-4 gap-3">
                      {relatedProducts.map(rp => (
                        <a key={rp.id} onClick={() => navigate(`/products/${rp.id}`)} className="bg-surface-container rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform shadow-sm">
                          <div className="aspect-square overflow-hidden">
                            {rp.imageId ? (<img src={`/api/images/${rp.imageId}`} alt={rp.name} className="w-full h-full object-contain" />) : (
                              <div className="w-full h-full bg-surface-container-high flex items-center justify-center"><span className="material-symbols-outlined text-3xl text-outline-variant">image</span></div>
                            )}
                          </div>
                          <div className="p-2.5">
                            <p className="text-xs font-bold text-on-surface truncate">{rp.name}</p>
                            <p className="text-xs font-bold text-primary mt-0.5">{formatCurrency(rp.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* RIGHT COLUMN — desktop only */}
              <div className="hidden tablet-md:block">
                <div className="sticky top-8 space-y-5">
                  <ProductInfoPanelV0 product={product} community={community} isOwner={isOwner} canReserve={canReserve} statusCfg={statusCfg} isWishlisted={isWishlisted} sellerProducts={sellerProducts} isCommunityAdmin={isCommunityAdmin} onReserve={() => setReservationDialog(true)} onContact={handleContactSeller} onEdit={() => navigate(`/products/${id}/edit`)} onDelete={() => setDeleteDialog(true)} onKick={() => setKickDialog(true)} onBan={() => setBanDialog(true)} navigate={navigate} t={t} />
                </div>
              </div>
            </div>
          </div>
        </main>
        {VariantSelector}
      </Shell>
    );
  };


  /* ═══════════════════════════════════════════════════════════════════════
     VARIANT 8 — "TW Con Sidebar" (DashboardSidebar, no header)
     ═══════════════════════════════════════════════════════════════════════ */
  const renderTWConSidebar = () => {
    const theme = themeVars;
    const statusCfg = statusCfgTW;

    const Shell = ({ children }) => (
      <div
        className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex"
        style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
      >
        <DashboardSidebar activePath="" />
        <div className="flex-1 flex flex-col overflow-hidden">
          {children}
        </div>
      </div>
    );

    if (loading) {
      return (
        <Shell>
          <main className="flex-1 overflow-y-auto px-4 tablet-sm:px-8 py-8 hide-scrollbar">
            <div className="max-w-6xl mx-auto">
              <div className="h-5 bg-surface-container-high rounded animate-pulse w-48 mb-6" />
              <div className="grid grid-cols-1 tablet-md:grid-cols-3 gap-8">
                <div className="tablet-md:col-span-2 space-y-4">
                  <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/30 p-4" style={{ height: '520px' }}>
                    <div className="w-full h-full bg-surface-container-high rounded-xl animate-pulse" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="h-10 bg-surface-container-high rounded-xl animate-pulse" />
                  <div className="h-12 bg-surface-container-high rounded-xl animate-pulse" />
                </div>
              </div>
            </div>
          </main>
          {VariantSelector}
        </Shell>
      );
    }

    if (error || !product) {
      return (
        <Shell>
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <span className="material-symbols-outlined text-6xl text-outline-variant mb-4 block">error_outline</span>
              <h2 className="text-lg font-bold text-on-surface mb-2">{error || t('Producto no encontrado')}</h2>
              <a onClick={() => navigate('/')} className="text-primary font-semibold cursor-pointer hover:underline">{t('Volver al inicio')}</a>
            </div>
          </main>
          {VariantSelector}
        </Shell>
      );
    }

    return (
      <Shell>
        <main className="flex-1 overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
          <div className="max-w-6xl mx-auto px-4 tablet-sm:px-8 py-6">
            {/* Two-column layout */}
            <div className="grid grid-cols-1 tablet-md:grid-cols-3 gap-8">
              {/* LEFT COLUMN */}
              <div className="tablet-md:col-span-2 space-y-6">
                {/* Image Card */}
                <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/30 overflow-hidden">
                  <div className="relative group px-3 pt-14 pb-3 tablet-sm:px-4 tablet-sm:pt-16 tablet-sm:pb-4">
                    <div className="relative cursor-zoom-in rounded-xl overflow-hidden" onClick={(e) => { if (!e.target.closest('.nav-btn')) setLightboxOpen(true); }}>
                      {images[selectedImage]?.url ? (
                        <img src={images[selectedImage].url} alt={images[selectedImage].alt} className="w-full object-contain" style={{ height: '500px' }} />
                      ) : (
                        <div className="w-full flex items-center justify-center bg-surface-container-high" style={{ height: '500px' }}>
                          <span className="material-symbols-outlined text-7xl text-outline-variant">image</span>
                        </div>
                      )}
                      {images.length > 1 && (
                        <>
                          <a className="nav-btn absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={(e) => { e.stopPropagation(); handlePreviousImage(); }}>
                            <span className="material-symbols-outlined text-on-surface">chevron_left</span>
                          </a>
                          <a className="nav-btn absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={(e) => { e.stopPropagation(); handleNextImage(); }}>
                            <span className="material-symbols-outlined text-on-surface">chevron_right</span>
                          </a>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 text-white text-sm rounded-full font-medium">{selectedImage + 1} / {images.length}</div>
                        </>
                      )}
                    </div>
                    <div className="absolute top-3 tablet-sm:top-4 left-3 tablet-sm:left-4 flex items-center gap-2">
                      <a
                        onClick={(e) => { e.stopPropagation(); navigate(-1); }}
                        className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors"
                        title={t('Volver')}
                        aria-label={t('Volver')}
                      >
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">arrow_back</span>
                      </a>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm ${statusCfg.classes}`}>
                        <span className="material-symbols-outlined text-[14px]">{statusCfg.icon}</span>
                        {t(statusCfg.label)}
                      </span>
                    </div>
                    <div className="absolute top-3 tablet-sm:top-4 right-3 tablet-sm:right-4 flex gap-2">
                      <a onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors">
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">fullscreen</span>
                      </a>
                      <a onClick={(e) => { e.stopPropagation(); handleWishlistToggle(); }} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors">
                        <span className="material-symbols-outlined text-[20px]" style={{ color: isWishlisted ? '#ef4444' : undefined, fontVariationSettings: isWishlisted ? "'FILL' 1" : undefined }}>favorite</span>
                      </a>
                      <a onClick={(e) => { e.stopPropagation(); handleShare(); }} className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm cursor-pointer hover:bg-white transition-colors">
                        <span className="material-symbols-outlined text-[20px] text-on-surface-variant">share</span>
                      </a>
                    </div>
                  </div>
                  {images.length > 1 && (
                    <div className="px-3 tablet-sm:px-4 pb-3 tablet-sm:pb-4">
                      <div className="flex gap-2.5 overflow-x-auto hide-scrollbar pt-2">
                        {images.map((img, index) => (
                          <a key={index} onClick={() => setSelectedImage(index)} className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${selectedImage === index ? 'border-primary shadow-sm' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                            <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mx-3 tablet-sm:mx-4 border-t border-outline-variant/30" />
                  <div className="px-4 tablet-sm:px-5 pt-5 pb-2">
                    <h1 className="text-2xl font-bold text-on-surface leading-tight mb-2">{product.name}</h1>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-2xl font-bold text-primary">{formatCurrency(product.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                      {product.condition && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">{t(CONDITION_LABELS[product.condition] || product.condition)}</span>
                      )}
                      {product.stock == null ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary/10 text-secondary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">all_inclusive</span>
                          {t('Stock ilimitado')}
                        </span>
                      ) : product.stock === 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-error/10 text-error text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Sin stock')}
                        </span>
                      ) : product.stock === 1 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Queda 1 unidad')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[11px] font-bold rounded-md uppercase">
                          <span className="material-symbols-outlined text-[14px]">inventory_2</span>
                          {t('Quedan {{count}} unidades', { count: product.stock })}
                        </span>
                      )}
                    </div>
                  </div>
                  {product.description && (
                    <div className="px-4 tablet-sm:px-5 pt-3 pb-5">
                      <p className="text-base text-on-surface-variant leading-relaxed whitespace-pre-wrap">{product.description}</p>
                    </div>
                  )}
                </div>
                {/* Mobile info panel */}
                <div className="tablet-md:hidden">
                  <ProductInfoPanelV0 product={product} community={community} isOwner={isOwner} canReserve={canReserve} statusCfg={statusCfg} isWishlisted={isWishlisted} sellerProducts={sellerProducts} isCommunityAdmin={isCommunityAdmin} onReserve={() => setReservationDialog(true)} onContact={handleContactSeller} onEdit={() => navigate(`/products/${id}/edit`)} onDelete={() => setDeleteDialog(true)} onKick={() => setKickDialog(true)} onBan={() => setBanDialog(true)} navigate={navigate} t={t} hideHeaderCard />
                </div>
                {/* Related Products */}
                {relatedProducts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-on-surface mb-4">{t('Mas de esta comunidad')}</h3>
                    <div className="grid grid-cols-2 mobile-md:grid-cols-4 gap-3">
                      {relatedProducts.map(rp => (
                        <a key={rp.id} onClick={() => navigate(`/products/${rp.id}`)} className="bg-surface-container rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform shadow-sm">
                          <div className="aspect-square overflow-hidden">
                            {rp.imageId ? (<img src={`/api/images/${rp.imageId}`} alt={rp.name} className="w-full h-full object-contain" />) : (
                              <div className="w-full h-full bg-surface-container-high flex items-center justify-center"><span className="material-symbols-outlined text-3xl text-outline-variant">image</span></div>
                            )}
                          </div>
                          <div className="p-2.5">
                            <p className="text-xs font-bold text-on-surface truncate">{rp.name}</p>
                            <p className="text-xs font-bold text-primary mt-0.5">{formatCurrency(rp.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* RIGHT COLUMN — desktop only */}
              <div className="hidden tablet-md:block">
                <div className="sticky top-8 space-y-5">
                  <ProductInfoPanelV0 product={product} community={community} isOwner={isOwner} canReserve={canReserve} statusCfg={statusCfg} isWishlisted={isWishlisted} sellerProducts={sellerProducts} isCommunityAdmin={isCommunityAdmin} onReserve={() => setReservationDialog(true)} onContact={handleContactSeller} onEdit={() => navigate(`/products/${id}/edit`)} onDelete={() => setDeleteDialog(true)} onKick={() => setKickDialog(true)} onBan={() => setBanDialog(true)} navigate={navigate} t={t} />
                </div>
              </div>
            </div>
          </div>
        </main>
        {VariantSelector}
      </Shell>
    );
  };


  /* ═══════════════════════════════════════════════════════════════════════
     MAIN RENDER — Switch on designVariant
     ═══════════════════════════════════════════════════════════════════════ */
  let variantContent;
  switch (designVariant) {
    case 0: variantContent = renderTWConSidebar(); break;
    case 1: variantContent = renderMUIVariant({ cardStyle: 'gray', sidebar: false }); break;
    case 2: variantContent = renderMUIVariant({ cardStyle: 'border', sidebar: true }); break;
    case 3: variantContent = renderMUIVariant({ cardStyle: 'shadow', sidebar: false }); break;
    case 4: variantContent = renderMUIVariant({ cardStyle: 'border', sidebar: false }); break;
    case 5: variantContent = renderMUIVariant({ cardStyle: 'shadow', sidebar: true }); break;
    case 6: variantContent = renderMUIVariant({ cardStyle: 'gray', sidebar: true }); break;
    case 7: variantContent = renderTWConHeader(); break;
    case 8: variantContent = renderTWSinHeader(); break;
    default: variantContent = renderTWConSidebar();
  }
  // SharedDialogs se renderiza una sola vez fuera del switch para evitar
  // que múltiples ramas del árbol monten copias duplicadas del Dialog/Snackbar
  // (cada copia comparte el mismo estado y aparecía superpuesta).
  return <>{variantContent}{sharedDialogs}</>;
};


/* ═══════════════════════════════════════════════════════════════════════════════
   V0 — Product Info Panel (Tailwind, right column desktop / inline mobile)
   ═══════════════════════════════════════════════════════════════════════════════ */
function ProductInfoPanelV0({ product, community, isOwner, canReserve, statusCfg, sellerProducts, isCommunityAdmin, onReserve, onContact, onEdit, onDelete, onKick, onBan, navigate, t, hideHeaderCard = false }) {
  const renderStatusAlerts = () => (
    <>
      {!isOwner && product.status === 'RESERVED' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800 font-medium">
          <span className="material-symbols-outlined text-[16px]">schedule</span>
          {t('Este producto esta reservado actualmente')}
        </div>
      )}
      {!isOwner && product.status === 'SOLD' && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium">
          <span className="material-symbols-outlined text-[16px]">sell</span>
          {t('Este producto ya fue vendido')}
        </div>
      )}
    </>
  );
  return (
    <div className="space-y-5">
      {!hideHeaderCard ? (
        /* Price + Name card (desktop right column) */
        <div className="bg-surface-container rounded-xl p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h1 className="text-xl font-bold text-on-surface leading-tight">{product.name}</h1>
          </div>
          <p className="text-2xl font-bold text-primary mb-1">
            {formatCurrency(product.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {product.condition && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[10px] font-bold rounded-md uppercase">
                {t(CONDITION_LABELS[product.condition] || product.condition)}
              </span>
            )}
            {product.stock == null ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary/10 text-secondary text-[10px] font-bold rounded-md uppercase">
                <span className="material-symbols-outlined text-[12px]">all_inclusive</span>
                {t('Stock ilimitado')}
              </span>
            ) : product.stock === 0 ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-error/10 text-error text-[10px] font-bold rounded-md uppercase">
                <span className="material-symbols-outlined text-[12px]">inventory_2</span>
                {t('Sin stock')}
              </span>
            ) : product.stock === 1 ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[10px] font-bold rounded-md uppercase">
                <span className="material-symbols-outlined text-[12px]">inventory_2</span>
                {t('Queda 1 unidad')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/8 text-primary text-[10px] font-bold rounded-md uppercase">
                <span className="material-symbols-outlined text-[12px]">inventory_2</span>
                {t('Quedan {{count}} unidades', { count: product.stock })}
              </span>
            )}
          </div>
          <div className="mt-4 space-y-2 empty:hidden">{renderStatusAlerts()}</div>
        </div>
      ) : (
        /* Mobile: only the status alerts (the title/price/condition already appear above the description) */
        <div className="space-y-2 empty:hidden">{renderStatusAlerts()}</div>
      )}

      {/* Action Buttons */}
      <div className="space-y-2">
        {canReserve && (
          <a
            onClick={onReserve}
            className="w-full py-3 bg-gradient-to-r from-primary to-primary-container text-white font-semibold rounded-xl active:scale-95 transition-transform duration-150 flex items-center justify-center gap-2 shadow-sm cursor-pointer"
          >
            <span className="material-symbols-outlined text-[18px]">shopping_cart</span>
            {t('Reservar producto')}
          </a>
        )}
        {!isOwner && onContact && (
          <a
            onClick={onContact}
            className="w-full py-3 border border-primary/40 text-primary font-semibold rounded-xl active:scale-95 transition-transform duration-150 flex items-center justify-center gap-2 cursor-pointer hover:bg-primary/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">forum</span>
            {t('Contactar al vendedor')}
          </a>
        )}
        {isOwner && (
          <>
            <a
              onClick={onEdit}
              className="w-full py-3 bg-primary text-on-primary font-semibold rounded-xl active:scale-95 transition-transform duration-150 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span className="material-symbols-outlined text-[18px]">edit</span>
              {t('Editar producto')}
            </a>
            <a
              onClick={onDelete}
              className="w-full py-2.5 border border-error/30 text-error font-medium rounded-xl flex items-center justify-center gap-2 cursor-pointer hover:bg-error/5 transition-colors text-sm"
            >
              <span className="material-symbols-outlined text-[16px]">delete</span>
              {t('Eliminar producto')}
            </a>
          </>
        )}
        {isCommunityAdmin && (
          <>
            <a
              onClick={onKick}
              className="w-full py-2.5 border border-yellow-400/50 text-yellow-700 font-medium rounded-xl flex items-center justify-center gap-2 cursor-pointer hover:bg-yellow-50 transition-colors text-sm"
            >
              <span className="material-symbols-outlined text-[16px]">person_remove</span>
              {t('Expulsar al vendedor de la comunidad')}
            </a>
            <a
              onClick={onBan}
              className="w-full py-2.5 border border-error/30 text-error font-medium rounded-xl flex items-center justify-center gap-2 cursor-pointer hover:bg-error/5 transition-colors text-sm"
            >
              <span className="material-symbols-outlined text-[16px]">block</span>
              {t('Bloquear al vendedor de la comunidad')}
            </a>
          </>
        )}
      </div>

      {/* Product metadata — side-by-side on mobile, stacked on desktop */}
      <div className="px-1 grid grid-cols-2 gap-3 tablet-md:grid-cols-1 tablet-md:gap-0 tablet-md:space-y-3">
        {product.condition && (
          <DetailItemV0 icon="new_releases" label={t('Estado')} value={t(CONDITION_LABELS[product.condition] || product.condition)} />
        )}
        <DetailItemV0 icon="calendar_today" label={t('Publicado')} value={formatDate(product.createdAt)} />
      </div>

      {/* Extra product details (only when present) */}
      {(product.category || product.brand || product.model || product.location) && (
        <div className="bg-surface-container rounded-xl p-4">
          <h4 className="text-xs font-bold text-on-surface mb-3">{t('Informacion del producto')}</h4>
          <div className="grid grid-cols-2 gap-3">
            {product.category && <DetailItemV0 icon="category" label={t('Categoria')} value={product.category} />}
            {product.brand && <DetailItemV0 icon="inventory_2" label={t('Marca')} value={product.brand} />}
            {product.model && <DetailItemV0 icon="devices" label={t('Modelo')} value={product.model} />}
            {product.location && <DetailItemV0 icon="location_on" label={t('Ubicacion')} value={product.location} />}
          </div>
        </div>
      )}

      {/* Seller Card */}
      {product.publisherName && !isOwner && (
        <div className="bg-surface-container rounded-xl p-4">
          <a
            onClick={() => navigate(`/users/${product.publisherId}`)}
            className="flex items-center gap-3 cursor-pointer group"
          >
            {product.publisherProfilePictureId ? (
              <img
                src={getImageUrl(product.publisherProfilePictureId)}
                alt=""
                className="w-10 h-10 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full text-white flex items-center justify-center font-bold text-sm shrink-0" style={{ backgroundColor: 'var(--chrome-avatar)' }}>
                {product.publisherName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-on-surface group-hover:text-primary transition-colors truncate">{product.publisherName}</p>
              <p className="text-[10px] text-on-surface-variant">{t('Ver perfil del vendedor')}</p>
            </div>
            <span className="material-symbols-outlined text-outline-variant group-hover:text-primary transition-colors">chevron_right</span>
          </a>
          {(() => {
            const names = product.sharedCommunityNames || [];
            const ids = product.sharedCommunityIds || [];
            const pairs = names
              .map((name, i) => ({ name, id: ids[i] }))
              .filter(({ name }) => name && name.trim().length > 0);
            if (pairs.length === 0) return null;
            const baseClass = 'inline-flex items-center gap-1 px-2.5 py-0.5 bg-primary-container text-on-primary-container text-[10px] font-bold rounded-full';
            return (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--chrome-border-divider)' }}>
                <p className="text-[10px] text-on-surface-variant mb-2 font-medium">{t('Comunidades compartidas')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {pairs.map(({ name, id: cid }, i) => (
                    cid == null ? (
                      <span key={i} className={baseClass}>
                        <span className="material-symbols-outlined text-[11px]">hub</span>
                        {name}
                      </span>
                    ) : (
                      <a
                        key={i}
                        onClick={(e) => { e.stopPropagation(); navigate(`/communities/${cid}`); }}
                        className={`${baseClass} cursor-pointer hover:opacity-85 transition-opacity`}
                      >
                        <span className="material-symbols-outlined text-[11px]">hub</span>
                        {name}
                      </a>
                    )
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Seller Schedule — UserSchedulesSection se autogatea (devuelve null
          si no hay horarios visibles). Si soy yo el publisher, muestra
          "Mis horarios" en lugar de "Dónde encontrar a X". */}
      {product.publisherName && product.publisherId && (
        <UserSchedulesSection
          userId={product.publisherId}
          userName={product.publisherName}
          isOwn={isOwner}
        />
      )}

      {/* More from Seller */}
      {sellerProducts.length > 0 && (
        <div className="bg-surface-container rounded-xl p-4">
          <h4 className="text-xs font-bold text-on-surface mb-3">{t('Mas de este vendedor')}</h4>
          <div className="space-y-2">
            {sellerProducts.map(sp => (
              <a
                key={sp.id}
                onClick={() => navigate(`/products/${sp.id}`)}
                className="flex items-center gap-3 p-1.5 rounded-lg cursor-pointer hover:bg-surface-container-high transition-colors"
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-surface-container-high">
                  {sp.imageId ? (
                    <img src={`/api/images/${sp.imageId}`} alt={sp.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-outline-variant text-lg">image</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-on-surface truncate">{sp.name}</p>
                  <p className="text-xs font-bold text-primary">
                    {formatCurrency(sp.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   V1 — Product Info Panel (MUI, right column desktop / inline mobile)
   ═══════════════════════════════════════════════════════════════════════════════ */
function ProductInfoPanelV1({ product, community, isOwner, canReserve, statusCfg, sellerProducts, isCommunityAdmin, onReserve, onContact, onEdit, onDelete, onKick, onBan, navigate, t, theme, cardSxOverride }) {

  const cardSx = cardSxOverride || {
    bgcolor: 'background.paper',
    borderRadius: 3,
    border: '1px solid',
    borderColor: alpha(theme.palette.divider, 0.5),
    p: 2.5,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Price + Name card */}
      <Box sx={cardSx}>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3, mb: 1 }}>
          {product.name}
        </Typography>
        <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 0.5 }}>
          {formatCurrency(product.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {product.condition && (
            <Chip
              label={t(CONDITION_LABELS[product.condition] || product.condition)}
              size="small"
              sx={{
                fontWeight: 700, fontSize: 10, height: 22,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                textTransform: 'uppercase',
              }}
            />
          )}
          <Chip
            label={
              product.stock == null
                ? t('Stock ilimitado')
                : product.stock === 0
                  ? t('Sin stock')
                  : product.stock === 1
                    ? t('Queda 1 unidad')
                    : t('Quedan {{count}} unidades', { count: product.stock })
            }
            size="small"
            sx={{
              fontWeight: 700, fontSize: 10, height: 22,
              bgcolor: product.stock === 0
                ? alpha(theme.palette.error.main, 0.10)
                : product.stock == null
                  ? alpha(theme.palette.secondary.main, 0.10)
                  : alpha(theme.palette.primary.main, 0.08),
              color: product.stock === 0
                ? 'error.main'
                : product.stock == null
                  ? 'secondary.main'
                  : 'primary.main',
              textTransform: 'uppercase',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Visibility sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {formatNumber(product.viewCount || 0)} {t('visitas')}
            </Typography>
          </Box>
        </Box>

        {/* Status alerts */}
        {!isOwner && product.status === 'RESERVED' && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: 13 } }}>
            {t('Este producto esta reservado actualmente')}
          </Alert>
        )}
        {!isOwner && product.status === 'SOLD' && (
          <Alert severity="error" variant="outlined" sx={{ mt: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: 13 } }}>
            {t('Este producto ya fue vendido')}
          </Alert>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {canReserve && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<ShoppingCart />}
            onClick={onReserve}
            sx={{
              py: 1.5, borderRadius: 3, fontWeight: 600, fontSize: 15,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
              '&:hover': { boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}` },
            }}
          >
            {t('Reservar producto')}
          </Button>
        )}
        {isOwner && (
          <>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<Edit />}
              onClick={onEdit}
              sx={{ py: 1.5, borderRadius: 3, fontWeight: 600, fontSize: 15 }}
            >
              {t('Editar producto')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              color="error"
              startIcon={<Delete />}
              onClick={onDelete}
              sx={{ py: 1.2, borderRadius: 3, fontWeight: 500, fontSize: 14, borderColor: alpha(theme.palette.error.main, 0.3) }}
            >
              {t('Eliminar producto')}
            </Button>
          </>
        )}
        {isCommunityAdmin && (
          <>
            <Button
              variant="outlined"
              fullWidth
              color="warning"
              onClick={onKick}
              sx={{ py: 1.2, borderRadius: 3, fontWeight: 500, fontSize: 14 }}
            >
              {t('Expulsar al vendedor de la comunidad')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              color="error"
              onClick={onBan}
              sx={{ py: 1.2, borderRadius: 3, fontWeight: 500, fontSize: 14 }}
            >
              {t('Bloquear al vendedor de la comunidad')}
            </Button>
          </>
        )}
      </Box>

      {/* Product Details Grid */}
      <Box sx={cardSx}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: 13 }}>
          {t('Informacion del producto')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {product.condition && (
            <DetailItemV1 icon={<NewReleases sx={{ fontSize: 18, color: 'text.secondary' }} />} label={t('Estado')} value={t(CONDITION_LABELS[product.condition] || product.condition)} />
          )}
          <DetailItemV1 icon={<CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />} label={t('Publicado')} value={formatDate(product.createdAt)} />
          {product.category && (
            <DetailItemV1 icon={<Category sx={{ fontSize: 18, color: 'text.secondary' }} />} label={t('Categoria')} value={product.category} />
          )}
          {product.brand && (
            <DetailItemV1 icon={<Inventory2 sx={{ fontSize: 18, color: 'text.secondary' }} />} label={t('Marca')} value={product.brand} />
          )}
          {product.model && (
            <DetailItemV1 icon={<Devices sx={{ fontSize: 18, color: 'text.secondary' }} />} label={t('Modelo')} value={product.model} />
          )}
          {product.location && (
            <DetailItemV1 icon={<LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />} label={t('Ubicacion')} value={product.location} />
          )}
          <DetailItemV1 icon={<Visibility sx={{ fontSize: 18, color: 'text.secondary' }} />} label={t('Visitas')} value={formatNumber(product.viewCount || 0)} />
        </Box>
      </Box>

      {/* Seller Card */}
      {product.publisherName && !isOwner && (
        <Box sx={cardSx}>
          <Box
            onClick={() => navigate(`/users/${product.publisherId}`)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
              '&:hover .seller-name': { color: 'primary.main' },
              '&:hover .seller-arrow': { color: 'primary.main' },
            }}
          >
            <Avatar
              src={product.publisherProfilePictureId ? getImageUrl(product.publisherProfilePictureId) : undefined}
              sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: 16, fontWeight: 700 }}
            >
              {product.publisherName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography className="seller-name" variant="body2" fontWeight={700} noWrap sx={{ transition: 'color 0.15s' }}>
                {product.publisherName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {t('Ver perfil del vendedor')}
              </Typography>
            </Box>
            <ChevronRight className="seller-arrow" sx={{ color: 'text.disabled', transition: 'color 0.15s' }} />
          </Box>

          {/* Shared communities */}
          {(() => {
            const sharedNames = (product.sharedCommunityNames || []).filter((n) => n && n.trim().length > 0);
            if (sharedNames.length === 0) return null;
            return (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: alpha(theme.palette.divider, 0.4) }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, fontWeight: 500, mb: 1, display: 'block' }}>
                  {t('Comunidades compartidas')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {sharedNames.map((name, i) => (
                    <Chip
                      key={i}
                      label={name}
                      size="small"
                      sx={{
                        fontSize: 11, height: 24, fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Seller Schedule (V1) — autogateado por UserSchedulesSection. */}
      {product.publisherName && product.publisherId && (
        <Box sx={{ ...cardSx, p: 0, bgcolor: 'transparent', boxShadow: 'none' }}>
          <UserSchedulesSection
            userId={product.publisherId}
            userName={product.publisherName}
            isOwn={isOwner}
          />
        </Box>
      )}

      {/* More from Seller */}
      {sellerProducts.length > 0 && (
        <Box sx={cardSx}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, fontSize: 13 }}>
            {t('Mas de este vendedor')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sellerProducts.map(sp => (
              <Box
                key={sp.id}
                onClick={() => navigate(`/products/${sp.id}`)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 2,
                  cursor: 'pointer', transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: alpha(theme.palette.action.hover, 1) },
                }}
              >
                <Box sx={{
                  width: 52, height: 52, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0,
                  bgcolor: alpha(theme.palette.divider, 0.15),
                }}>
                  {sp.imageId ? (
                    <Box component="img" src={`/api/images/${sp.imageId}`} alt={sp.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" color="text.disabled">?</Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" fontWeight={500} noWrap sx={{ display: 'block' }}>
                    {sp.name}
                  </Typography>
                  <Typography variant="caption" fontWeight={700} color="primary">
                    {formatCurrency(sp.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}


/* ── Small helpers ── */
function DetailItemV0({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="material-symbols-outlined text-[16px] text-on-surface-variant mt-0.5">{icon}</span>
      <div>
        <p className="text-[10px] text-on-surface-variant font-medium">{label}</p>
        <p className="text-sm text-on-surface font-medium">{value}</p>
      </div>
    </div>
  );
}

function DetailItemV1({ icon, label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ mt: 0.3 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, fontWeight: 500, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

export default ProductDetails;
